import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import AdminDashboard from './AdminDashboard';
import AdminProducts from './AdminProducts';
import AdminCategories from './AdminCategories';
import AdminGoldRates from './AdminGoldRates';
import AdminPriceCalculator from './AdminPriceCalculator';
import AdminOrders from './AdminOrders';
import AdminCustomers from './AdminCustomers';
import AdminInventory from './AdminInventory';
import AdminPaymentsGST from './AdminPaymentsGST';
import AdminShipping from './AdminShipping';
import AdminCoupons from './AdminCoupons';
import AdminSEOMarketing from './AdminSEOMarketing';
import AdminOldGold from './AdminOldGold';
import AdminCustomOrders from './AdminCustomOrders';
import AdminReports from './AdminReports';
import AdminStaff from './AdminStaff';
import AdminSettings from './AdminSettings';
import AdminDatabaseHub from './AdminDatabaseHub';
import AdminLoginModal from './AdminLoginModal';
import AdminInvoiceModal from './AdminInvoiceModal';
import AdminInquiries from './AdminInquiries';

// Visual CMS Components
import AdminHeroManager from './AdminHeroManager';
import AdminAnnouncements from './AdminAnnouncements';
import AdminStoriesManager from './AdminStoriesManager';
import AdminTestimonialsManager from './AdminTestimonialsManager';
import AdminTrustManager from './AdminTrustManager';
import AdminPopupManager from './AdminPopupManager';
import AdminStoreLocator from './AdminStoreLocator';

import { 
  LayoutDashboard, 
  Gem, 
  Layers,
  ShoppingBag, 
  TrendingUp, 
  Users, 
  Store, 
  ExternalLink,
  LogOut, 
  Menu, 
  X,
  Sparkles,
  ShieldCheck, 
  Tag, 
  Settings, 
  Image as ImageIcon, 
  Megaphone, 
  CircleDot, 
  Star, 
  Award, 
  Gift, 
  Palette, 
  Database, 
  RefreshCw, 
  MapPin,
  Calculator,
  Package,
  Receipt,
  Truck,
  Repeat,
  BarChart3,
  Globe,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { formatINR } from '../../data/products';

export default function AdminLayout() {
  const { 
    isAdminAuthenticated, 
    logoutAdmin, 
    setIsAdminMode, 
    orders, 
    consultations,
    liveGoldRates,
    storeSettings,
    dbStatus,
    isSyncingDb,
    syncDatabase
  } = useShop();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cmsExpanded, setCmsExpanded] = useState(false);

  // If not authenticated, show secure Admin Login Gateway
  if (!isAdminAuthenticated) {
    return <AdminLoginModal />;
  }

  const safeOrders = Array.isArray(orders) ? orders : [];
  const safeConsultations = Array.isArray(consultations) ? consultations : [];
  const pendingOrdersCount = safeOrders.filter(o => o?.status === 'Processing' || o?.status === 'In Vault Transit').length;
  const newLeadsCount = safeConsultations.filter(c => c?.status === 'New Lead' || c?.status === 'Scheduled').length;

  // 17-Point Menu Structure matching Developer Specification
  const coreTabs = [
    { id: 'dashboard', label: '1. Dashboard', icon: <LayoutDashboard size={16} /> },
    { id: 'products', label: '2. Products Catalog', icon: <Gem size={16} /> },
    { id: 'categories', label: '— Master Categories', icon: <Layers size={16} /> },
    { id: 'gold-rates', label: '3. Live Gold / Silver Rates', icon: <TrendingUp size={16} />, badge: 'IBJA Spot' },
    { id: 'price-calc', label: '4. Price & Wastage Calculator', icon: <Calculator size={16} /> },
    { id: 'orders', label: '5. Orders & Returns', icon: <ShoppingBag size={16} />, badge: pendingOrdersCount > 0 ? pendingOrdersCount : null },
    { id: 'customers', label: '6. Customers & CRM (VIP)', icon: <Users size={16} /> },
    { id: 'inventory', label: '7. Inventory & Suppliers', icon: <Package size={16} /> },
    { id: 'payments-gst', label: '8. Payments & GST Invoices', icon: <Receipt size={16} /> },
    { id: 'shipping', label: '9. Shipping & Tracking', icon: <Truck size={16} /> },
    { id: 'coupons', label: '10. Offers & Coupons', icon: <Tag size={16} /> },
    { id: 'banners', label: '11. Banners & Home Sliders', icon: <ImageIcon size={16} />, badge: 'Upload / Add' },
  ];

  const advancedJewelleryTabs = [
    { id: 'old-gold', label: '13. Old Gold Exchange', icon: <Repeat size={16} /> },
    { id: 'custom-orders', label: '14. Custom Orders & Rate Locks', icon: <Sparkles size={16} /> },
  ];

  const marketingGrowthTabs = [
    { id: 'seo-marketing', label: '12. SEO & Marketing', icon: <Globe size={16} /> },
    { id: 'inquiries', label: '— VIP Bridal Consultations', icon: <Users size={16} />, badge: newLeadsCount > 0 ? newLeadsCount : null },
  ];

  const adminSystemTabs = [
    { id: 'reports', label: '15. Reports & Analytics', icon: <BarChart3 size={16} /> },
    { id: 'staff', label: '16. Staff & Permissions (2FA)', icon: <ShieldCheck size={16} /> },
    { id: 'settings', label: '17. Store Settings & Policies', icon: <Settings size={16} /> },
    { id: 'database', label: '— Database & Cloud Sync', icon: <Database size={16} />, badge: dbStatus?.isConnected ? 'Live' : 'Offline' },
  ];

  const cmsSubTabs = [
    { id: 'cms-hero', label: 'Hero Banner Slider', icon: <ImageIcon size={14} /> },
    { id: 'cms-announcements', label: 'Announcements & Ticker', icon: <Megaphone size={14} /> },
    { id: 'cms-stories', label: 'Story Highlights', icon: <CircleDot size={14} /> },
    { id: 'cms-testimonials', label: 'Client Reviews', icon: <Star size={14} /> },
    { id: 'cms-trust', label: 'Trust & Hallmark Pillars', icon: <Award size={14} /> },
    { id: 'cms-popup', label: 'Festive Promo Popup', icon: <Gift size={14} /> },
    { id: 'cms-store-locator', label: 'Showroom Location', icon: <MapPin size={14} /> },
  ];

  const renderTabButton = (tab) => {
    const isActive = activeTab === tab.id;
    return (
      <button
        key={tab.id}
        onClick={() => {
          setActiveTab(tab.id);
          setSidebarOpen(false);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.65rem',
          padding: '0.55rem 0.85rem',
          borderRadius: isActive ? '0 4px 4px 0' : '4px',
          border: 'none',
          borderLeft: isActive ? '4px solid #2874f0' : '4px solid transparent',
          background: isActive ? '#e8f0fe' : 'transparent',
          color: isActive ? '#2874f0' : '#424242',
          fontWeight: isActive ? 700 : 500,
          fontSize: '0.81rem',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'all 0.15s ease',
          position: 'relative',
          width: '100%'
        }}
      >
        <span style={{ color: isActive ? '#2874f0' : '#757575', display: 'flex' }}>{tab.icon}</span>
        <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tab.label}</span>
        {tab.badge && (
          <span style={{
            background: isActive ? '#2874f0' : '#fb641b',
            color: '#ffffff',
            fontSize: '0.62rem',
            fontWeight: 700,
            padding: '0.1rem 0.4rem',
            borderRadius: '999px'
          }}>
            {tab.badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="admin-fk-portal" style={{ minHeight: '100vh', backgroundColor: '#f1f3f6', display: 'flex', flexDirection: 'column' }}>
      
      {/* Top Admin Navbar - Flipkart Blue */}
      <header style={{
        height: '62px',
        backgroundColor: '#2874f0',
        boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 1.5rem',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        {/* Left: Mobile Menu & Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          <button
            className="btn-icon mobile-sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{ display: 'none', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}
          >
            {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '2px',
              background: '#ffe500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 900,
              fontStyle: 'italic',
              color: '#2874f0',
              fontSize: '1.2rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
            }}>
              ML
            </div>
            <div>
              <span style={{ fontWeight: 800, fontStyle: 'italic', color: '#ffffff', fontSize: '1.2rem', letterSpacing: '-0.5px' }}>
                M.L. JEWELLERS
              </span>
              <span style={{
                display: 'inline-block',
                marginLeft: '0.6rem',
                fontSize: '0.68rem',
                padding: '0.15rem 0.55rem',
                background: '#ffe500',
                color: '#1a5bb8',
                borderRadius: '2px',
                fontWeight: 800,
                letterSpacing: '0.5px'
              }}>
                SELLER HUB & VAULT ADMIN
              </span>
            </div>
          </div>
        </div>

        {/* Right: Bullion pill, DB status, Storefront, and Logout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Database Live Status Badge */}
          <button
            type="button"
            onClick={() => setActiveTab('database')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.45rem',
              background: 'rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '2px',
              padding: '0.3rem 0.75rem',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#ffffff',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            title="Click to open Database Command Center"
          >
            <Database size={13} />
            <span style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: dbStatus?.isConnected ? '#34d399' : '#ef4444',
              boxShadow: dbStatus?.isConnected ? '0 0 6px #34d399' : 'none'
            }} />
            <span>
              {dbStatus?.isConnected 
                ? `DB Connected` 
                : 'DB Offline'}
            </span>
          </button>

          {/* Quick Database Sync Trigger */}
          <button
            onClick={() => syncDatabase(false)}
            disabled={isSyncingDb}
            title="Re-synchronize with MongoDB Database"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              fontSize: '0.75rem',
              padding: '0.32rem 0.75rem',
              background: '#ffffff',
              color: '#2874f0',
              fontWeight: 700,
              border: 'none',
              borderRadius: '2px',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
          >
            <RefreshCw size={12} style={{ animation: isSyncingDb ? 'spin 1s linear infinite' : 'none' }} />
            <span>{isSyncingDb ? 'Syncing...' : 'Sync DB'}</span>
          </button>

          {/* Gold Rate Pill */}
          <div className="admin-gold-pill" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            background: 'rgba(0, 0, 0, 0.18)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            borderRadius: '2px',
            padding: '0.32rem 0.8rem',
            fontSize: '0.76rem',
            color: '#ffffff',
            fontWeight: 600
          }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ffe500' }} />
            <span>24K: <strong style={{ color: '#ffe500' }}>{formatINR(liveGoldRates.gold24K)}/g</strong> | Silver: <strong style={{ color: '#ffe500' }}>₹{liveGoldRates.silver999}/g</strong></span>
          </div>

          {/* Visit Storefront (Same Tab) */}
          <button
            onClick={() => {
              setIsAdminMode(false);
              if (typeof window !== 'undefined') {
                window.location.hash = '#/';
              }
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.32rem 0.8rem',
              backgroundColor: 'transparent',
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.5)',
              borderRadius: '2px',
              fontSize: '0.76rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.15s ease'
            }}
            title="Switch to Customer Storefront (Home Page)"
          >
            <Store size={13} />
            <span>Storefront</span>
          </button>

          {/* Open Storefront in New Tab */}
          <a
            href="#/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.32rem 0.65rem',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.35)',
              borderRadius: '2px',
              fontSize: '0.74rem',
              fontWeight: 600,
              textDecoration: 'none',
              transition: 'background 0.15s ease'
            }}
            title="Open Customer Storefront in New Window / Tab"
          >
            <ExternalLink size={12} />
            <span>New Tab ↗</span>
          </a>

          <button
            onClick={logoutAdmin}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.32rem 0.8rem',
              backgroundColor: '#fb641b',
              color: '#ffffff',
              border: 'none',
              borderRadius: '2px',
              fontSize: '0.76rem',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 1px 2px 0 rgba(0,0,0,0.2)'
            }}
            title="Logout Admin Session"
          >
            <LogOut size={13} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      {/* Main Body */}
      <div style={{ display: 'flex', flex: 1, minHeight: 'calc(100vh - 62px)' }}>
        
        {/* Left Sidebar - Flipkart Style White Sidebar with Scroll */}
        <aside
          className={`admin-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}
          style={{
            width: '260px',
            backgroundColor: '#ffffff',
            borderRight: '1px solid #e0e0e0',
            padding: '1rem 0.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.2rem',
            flexShrink: 0,
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 62px)',
            boxShadow: '1px 0 3px 0 rgba(0,0,0,0.04)'
          }}
        >
          {/* Admin Profile Chip */}
          <div style={{
            background: '#f0f5ff',
            border: '1px solid #c7d9fc',
            borderRadius: '2px',
            padding: '0.65rem 0.8rem',
            marginBottom: '0.6rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.55rem'
          }}>
            <div style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: '#2874f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ShieldCheck size={15} color="#ffffff" />
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#212121', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {storeSettings.adminEmail || 'admin@mljewellers.com'}
              </div>
              <div style={{ fontSize: '0.65rem', color: '#388e3c', fontWeight: 700 }}>
                ● Super Admin (2FA Active)
              </div>
            </div>
          </div>

          {/* Section 1: Core Commerce Modules (1-10) */}
          <div style={{
            fontSize: '0.65rem',
            color: '#878787',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            padding: '0.35rem 0.6rem 0.15rem'
          }}>
            Store & Commerce (Phase 1 & 2)
          </div>
          {coreTabs.map(renderTabButton)}

          {/* Section 2: Jewellery Business Advanced (13, 14) */}
          <div style={{
            fontSize: '0.65rem',
            color: '#878787',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            padding: '0.8rem 0.6rem 0.15rem',
            borderTop: '1px solid #f0f0f0',
            marginTop: '0.3rem'
          }}>
            Jewellery Advanced Engine
          </div>
          {advancedJewelleryTabs.map(renderTabButton)}

          {/* Section 3: CMS & Marketing (11, 12) */}
          <div style={{
            fontSize: '0.65rem',
            color: '#2874f0',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            padding: '0.8rem 0.6rem 0.15rem',
            borderTop: '1px solid #f0f0f0',
            marginTop: '0.3rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Palette size={12} />
              <span>11. Homepage & CMS</span>
            </div>
            <button
              type="button"
              onClick={() => setCmsExpanded(!cmsExpanded)}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#2874f0' }}
            >
              {cmsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          </div>

          {/* Collapsible CMS Subtabs */}
          {cmsExpanded ? (
            <div style={{ background: '#f8fafc', borderRadius: '4px', padding: '0.2rem 0' }}>
              {cmsSubTabs.map(renderTabButton)}
            </div>
          ) : (
            <button
              onClick={() => {
                setCmsExpanded(true);
                setActiveTab('cms-hero');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.65rem',
                padding: '0.45rem 0.85rem',
                border: 'none',
                background: 'transparent',
                color: '#616161',
                fontSize: '0.78rem',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              <ImageIcon size={14} />
              <span>Manage Visual CMS (7 Sections)</span>
            </button>
          )}

          {marketingGrowthTabs.map(renderTabButton)}

          {/* Section 4: System & Compliance (15, 16, 17) */}
          <div style={{
            fontSize: '0.65rem',
            color: '#878787',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            padding: '0.8rem 0.6rem 0.15rem',
            borderTop: '1px solid #f0f0f0',
            marginTop: '0.3rem'
          }}>
            Audit, Staff & Settings
          </div>
          {adminSystemTabs.map(renderTabButton)}

          {/* Logout */}
          <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid #f0f0f0' }}>
            <button
              onClick={logoutAdmin}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                width: '100%',
                padding: '0.6rem 0.85rem',
                background: 'transparent',
                border: 'none',
                color: '#d32f2f',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                textAlign: 'left',
                borderRadius: '2px'
              }}
            >
              <LogOut size={14} />
              <span>Logout Admin Session</span>
            </button>
          </div>
        </aside>

        {/* Content Area - Flipkart Gray Background */}
        <main style={{ flex: 1, padding: '1.5rem 2rem', overflowX: 'hidden', backgroundColor: '#f1f3f6' }}>
          {/* Operations & Products */}
          {activeTab === 'dashboard' && <AdminDashboard setActiveTab={setActiveTab} />}
          {activeTab === 'products' && <AdminProducts />}
          {activeTab === 'categories' && <AdminCategories />}
          {activeTab === 'gold-rates' && <AdminGoldRates />}
          
          {/* Price Calculator */}
          {activeTab === 'price-calc' && <AdminPriceCalculator />}

          {/* Orders, Customers, Inventory & Accounting */}
          {activeTab === 'orders' && <AdminOrders />}
          {activeTab === 'customers' && <AdminCustomers />}
          {activeTab === 'inventory' && <AdminInventory />}
          {activeTab === 'payments-gst' && <AdminPaymentsGST />}
          {activeTab === 'shipping' && <AdminShipping />}
          {activeTab === 'coupons' && <AdminCoupons />}

          {/* Jewellery Advanced Modules */}
          {activeTab === 'old-gold' && <AdminOldGold />}
          {activeTab === 'custom-orders' && <AdminCustomOrders />}

          {/* Marketing & Inquiries */}
          {activeTab === 'seo-marketing' && <AdminSEOMarketing />}
          {activeTab === 'inquiries' && <AdminInquiries />}

          {/* Visual CMS Tabs */}
          {(activeTab === 'banners' || activeTab === 'cms-hero') && <AdminHeroManager />}
          {activeTab === 'cms-announcements' && <AdminAnnouncements />}
          {activeTab === 'cms-stories' && <AdminStoriesManager />}
          {activeTab === 'cms-testimonials' && <AdminTestimonialsManager />}
          {activeTab === 'cms-trust' && <AdminTrustManager />}
          {activeTab === 'cms-popup' && <AdminPopupManager />}
          {activeTab === 'cms-store-locator' && <AdminStoreLocator />}

          {/* System Settings & Reports */}
          {activeTab === 'reports' && <AdminReports />}
          {activeTab === 'staff' && <AdminStaff />}
          {activeTab === 'database' && <AdminDatabaseHub />}
          {activeTab === 'settings' && <AdminSettings />}
        </main>
      </div>

      {/* Official Tax Invoice & BIS Hallmark Modal */}
      <AdminInvoiceModal />

      <style>{`
        @media (max-width: 860px) {
          .admin-sidebar {
            position: fixed;
            top: 68px;
            left: 0;
            bottom: 0;
            z-index: 100;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            box-shadow: 10px 0 30px rgba(0, 0, 0, 0.8);
          }
          .admin-sidebar.sidebar-open {
            transform: translateX(0);
          }
          .mobile-sidebar-toggle {
            display: inline-flex !important;
          }
          .admin-gold-pill {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
